import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Stat, StatusBadge, btn, input, Field, Modal } from "@/components/kit";
import { toast } from "sonner";
import { Plus, Building2, Trash2, Ban, CheckCircle2, LayoutTemplate, CreditCard, Mail, Loader2, BarChart3, UserCog, Pencil, Wallet, Percent } from "lucide-react";

const PLAN_OPTS = ["starter", "growth", "scale"];
const TABS = [
  { k: "orgs", l: "Organizations", icon: Building2 },
  { k: "admins", l: "Super-admins", icon: UserCog },
  { k: "analytics", l: "Analytics", icon: BarChart3 },
  { k: "cms", l: "Home page", icon: LayoutTemplate },
  { k: "plans", l: "Plans", icon: CreditCard },
  { k: "payments", l: "Payments", icon: Wallet },
  { k: "email", l: "Email / SMTP", icon: Mail },
];

function PaymentsSettings() {
  const [st, setSt] = useState(null);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [fees, setFees] = useState(null);
  const [savingFees, setSavingFees] = useState(false);
  const [report, setReport] = useState(null);
  const load = () => {
    api.get("/platform/stripe").then((r) => setSt(r.data)).catch(() => {});
    api.get("/platform/connect-fees").then((r) => setFees(r.data)).catch(() => {});
    api.get("/platform/fee-report").then((r) => setReport(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.post("/platform/stripe", { secret_key: key }); toast.success(data.configured ? `Saved — ${data.mode} mode${data.account ? ` (${data.account})` : ""}` : "Cleared"); setKey(""); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const saveFees = async () => {
    setSavingFees(true);
    try { await api.post("/platform/connect-fees", fees); toast.success("Application fees saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSavingFees(false); }
  };
  if (!st || !fees) return <Loader2 className="h-5 w-5 animate-spin text-accent" />;
  const setFee = (k, v) => setFees({ ...fees, [k]: v });
  return (
    <div className="max-w-2xl space-y-6">
      <div className="bg-card border border-border p-6 space-y-4">
        <div>
          <p className="text-sm font-medium">Platform Stripe account</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">This is the platform's own Stripe account. It powers subscription billing, Stripe Connect onboarding for businesses, and is the fallback for businesses that haven't added their own key. Enable Connect in your Stripe dashboard to let businesses link accounts.</p>
        </div>
        {st.configured && (
          <div className="text-sm" data-testid="platform-stripe-status">
            {st.error ? <span className="text-destructive">{st.error}</span>
              : <span className="inline-flex items-center gap-1.5 text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" /> Connected ({st.mode} mode{st.account ? ` · ${st.account}` : ""}) · key ending {st.last4}</span>}
          </div>
        )}
        <Field label="Stripe secret key" hint="Starts with sk_. Stored encrypted. Leave blank and save to clear.">
          <input className={input} type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} placeholder={st.configured ? `•••• ${st.last4}` : "sk_live_… or sk_test_…"} data-testid="platform-stripe-key" />
        </Field>
        <button onClick={save} disabled={saving} className={btn.primary} data-testid="platform-stripe-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save Stripe key</button>
      </div>

      <div className="bg-card border border-border p-6 space-y-4">
        <div>
          <p className="text-sm font-medium">Application fees (Stripe Connect)</p>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">Take a percentage of each payment a business collects through their connected Stripe account, by plan. You can waive fees for individual businesses from the Organizations tab.</p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={!!fees.enabled} onChange={(e) => setFee("enabled", e.target.checked)} data-testid="fees-enabled" /> Charge application fees</label>
        <div className="grid grid-cols-3 gap-3">
          {["starter", "growth", "scale"].map((p) => (
            <Field key={p} label={`${p[0].toUpperCase() + p.slice(1)} fee %`}>
              <input type="number" step="0.1" min="0" max="100" className={input} value={fees[p] ?? 0} onChange={(e) => setFee(p, parseFloat(e.target.value) || 0)} data-testid={`fee-${p}`} disabled={!fees.enabled} />
            </Field>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-3">
          {["starter", "growth", "scale"].map((p) => (
            <Field key={p} label={`${p[0].toUpperCase() + p.slice(1)} flat £`}>
              <input type="number" step="0.01" min="0" className={input} value={fees[`flat_${p}`] ?? 0} onChange={(e) => setFee(`flat_${p}`, parseFloat(e.target.value) || 0)} data-testid={`flat-${p}`} disabled={!fees.enabled} />
            </Field>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">Total fee per payment = percentage of the amount + the flat fee, by the business's plan.</p>
        <button onClick={saveFees} disabled={savingFees} className={btn.primary} data-testid="fees-save">{savingFees ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save fees</button>
        {report && (
          <div className="border-t border-border pt-4 mt-2" data-testid="fee-report">
            <p className="overline text-muted-foreground">Fees collected to date</p>
            <p className="text-3xl font-light tracking-tighter mt-1">£{report.total_gbp.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground">across {report.payments} paid transaction{report.payments === 1 ? "" : "s"}</p>
            {report.by_org?.length > 0 && (
              <div className="grid sm:grid-cols-2 gap-6 mt-5">
                <div>
                  <p className="overline text-muted-foreground mb-2">By business</p>
                  <div className="space-y-1">
                    {report.by_org.map((r) => (
                      <div key={r.org_id} className="flex justify-between text-sm" data-testid={`fee-by-org-${r.org_id}`}>
                        <span className="truncate pr-2">{r.name}</span><span className="tabular-nums font-medium">£{r.fees_gbp.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="overline text-muted-foreground mb-2">By month</p>
                  <div className="space-y-1">
                    {report.by_month.map((r) => (
                      <div key={r.month} className="flex justify-between text-sm" data-testid={`fee-by-month-${r.month}`}>
                        <span>{r.month}</span><span className="tabular-nums font-medium">£{r.fees_gbp.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Admins() {
  const [admins, setAdmins] = useState([]);
  const [activity, setActivity] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "" });
  const [edit, setEdit] = useState(null);
  const [ef, setEf] = useState({ name: "", email: "", password: "" });
  const [fActor, setFActor] = useState("");
  const [fAction, setFAction] = useState("");

  const load = () => {
    api.get("/platform/admins").then((r) => setAdmins(r.data)).catch(() => {});
    api.get("/platform/admin-activity").then((r) => setActivity(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try { await api.post("/platform/admins", f); toast.success("Super-admin added"); setOpenCreate(false); setF({ name: "", email: "", password: "" }); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const openEdit = (a) => { setEdit(a); setEf({ name: a.name || "", email: a.email || "", password: "" }); };
  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = { name: ef.name, email: ef.email };
      if (ef.password) payload.password = ef.password;
      await api.patch(`/platform/admins/${edit.id}`, payload);
      toast.success("Saved"); setEdit(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const del = async (a) => {
    if (!window.confirm(`Remove super-admin "${a.name}"? They will lose platform access.`)) return;
    try { await api.delete(`/platform/admins/${a.id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };
  const rotate = async () => {
    if (!window.confirm("Re-encrypt all stored Stripe keys with the current primary encryption key? Add the new key to SECRET_ENCRYPTION_KEYS (keeping the old) and restart first.")) return;
    try { const { data } = await api.post("/platform/rotate-encryption"); toast.success(`Re-encrypted ${data.rotated} stored key(s)`); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-6"><button className={btn.primary} onClick={() => setOpenCreate(true)} data-testid="platform-create-admin"><Plus className="h-4 w-4" /> New super-admin</button></div>
      <div className="bg-card border border-border overflow-x-auto thin-scroll">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-6 py-3 font-medium">Name</th><th className="px-6 py-3 font-medium">Email</th><th className="px-6 py-3 font-medium text-right">Actions</th>
          </tr></thead>
          <tbody>
            {admins.map((a) => (
              <tr key={a.id} className="border-b border-border last:border-0" data-testid={`admin-row-${a.id}`}>
                <td className="px-6 py-3 font-medium">{a.name} {a.is_self && <span className="ml-1 text-xs text-muted-foreground">(you)</span>}</td>
                <td className="px-6 py-3 text-muted-foreground">{a.email}</td>
                <td className="px-6 py-3">
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => openEdit(a)} className={btn.outline} data-testid={`admin-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /> Edit</button>
                    {!a.is_self && admins.length > 1 && (
                      <button onClick={() => del(a)} className={btn.ghost + " text-destructive"} data-testid={`admin-delete-${a.id}`}><Trash2 className="h-4 w-4" /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {admins.length === 0 && <tr><td colSpan={3} className="px-6 py-10 text-center text-muted-foreground">No super-admins yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="mt-8">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <p className="overline text-muted-foreground">Activity log</p>
          <div className="flex items-center gap-2">
            <select className={input + " !py-1.5 text-sm"} value={fActor} onChange={(e) => setFActor(e.target.value)} data-testid="activity-filter-actor">
              <option value="">All people</option>
              {[...new Set(activity.map((a) => a.actor_email).filter(Boolean))].map((e) => <option key={e} value={e}>{e}</option>)}
            </select>
            <select className={input + " !py-1.5 text-sm"} value={fAction} onChange={(e) => setFAction(e.target.value)} data-testid="activity-filter-action">
              <option value="">All actions</option>
              <option value="created">Created</option>
              <option value="updated">Updated</option>
              <option value="removed">Removed</option>
            </select>
          </div>
        </div>
        <div className="bg-card border border-border divide-y divide-border" data-testid="admin-activity-log">
          {(() => {
            const rows = activity.filter((a) => (!fActor || a.actor_email === fActor) && (!fAction || a.action === fAction));
            if (rows.length === 0) return <div className="px-6 py-8 text-center text-muted-foreground text-sm">No matching activity.</div>;
            return rows.map((a) => (
              <div key={a.id} className="px-6 py-3 text-sm flex items-center justify-between gap-4" data-testid="admin-activity-row">
                <span>
                  <span className="font-medium">{a.actor_name || a.actor_email}</span>{" "}
                  <span className={a.action === "removed" ? "text-destructive" : a.action === "created" ? "text-emerald-600" : "text-muted-foreground"}>{a.action}</span>{" "}
                  super-admin <span className="font-medium">{a.target_name || a.target_email}</span>
                  {a.detail ? <span className="text-muted-foreground"> ({a.detail})</span> : null}
                </span>
                <span className="text-xs text-muted-foreground tabular-nums shrink-0">{new Date(a.at).toLocaleString()}</span>
              </div>
            ));
          })()}
        </div>
      </div>

      <div className="mt-8 bg-card border border-border p-6">
        <p className="text-sm font-medium">Encryption key rotation</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-xl">Businesses' Stripe keys are encrypted at rest. To rotate the platform encryption key: add the new key as the first entry in <code>SECRET_ENCRYPTION_KEYS</code> (keep the old key too), restart, then re-encrypt all stored keys below. Once done, you can remove the old key.</p>
        <button onClick={rotate} className={btn.outline + " mt-3"} data-testid="rotate-encryption">Re-encrypt stored keys</button>
      </div>

      <Modal open={openCreate} onClose={() => setOpenCreate(false)} title="Add super-admin" testid="create-admin-modal">
        <form onSubmit={create} className="space-y-4">
          <Field label="Name"><input required className={input} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="admin-name" /></Field>
          <Field label="Email"><input type="email" required className={input} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="admin-email" /></Field>
          <Field label="Password" hint="At least 6 characters."><input type="password" required minLength={6} className={input} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="admin-password" /></Field>
          <button type="submit" className={btn.primary} data-testid="admin-create-submit">Add super-admin</button>
        </form>
      </Modal>

      <Modal open={!!edit} onClose={() => setEdit(null)} title="Edit super-admin" testid="edit-admin-modal">
        <form onSubmit={saveEdit} className="space-y-4">
          <Field label="Name"><input required className={input} value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} data-testid="admin-edit-name" /></Field>
          <Field label="Email"><input type="email" required className={input} value={ef.email} onChange={(e) => setEf({ ...ef, email: e.target.value })} data-testid="admin-edit-email" /></Field>
          <Field label="New password" hint="Leave blank to keep the current password."><input type="password" className={input} value={ef.password} onChange={(e) => setEf({ ...ef, password: e.target.value })} placeholder="••••••" data-testid="admin-edit-password" /></Field>
          <button type="submit" className={btn.primary} data-testid="admin-edit-submit">Save changes</button>
        </form>
      </Modal>
    </div>
  );
}

function Orgs() {
  const [stats, setStats] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [openCreate, setOpenCreate] = useState(false);
  const [f, setF] = useState({ org_name: "", industry: "general", admin_name: "", email: "", password: "", plan: "starter" });

  const load = () => {
    api.get("/platform/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/platform/orgs").then((r) => setOrgs(r.data)).catch(() => {});
  };
  useEffect(load, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/platform/orgs", f);
      toast.success("Organization created");
      setOpenCreate(false);
      setF({ org_name: "", industry: "general", admin_name: "", email: "", password: "", plan: "starter" });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };
  const update = async (id, patch) => { try { await api.patch(`/platform/orgs/${id}`, patch); toast.success("Updated"); load(); } catch (e) { toast.error(apiError(e)); } };
  const del = async (id, name) => {
    if (!window.confirm(`Delete "${name}" and all its data?`)) return;
    try { await api.delete(`/platform/orgs/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-6"><button className={btn.primary} onClick={() => setOpenCreate(true)} data-testid="platform-create-org"><Plus className="h-4 w-4" /> New organization</button></div>
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-border border border-border mb-8">
          <Stat label="Organizations" value={stats.total_orgs} />
          <Stat label="Active subs" value={stats.active_subscriptions} accent />
          <Stat label="Trialing" value={stats.trialing} />
          <Stat label="MRR" value={`£${stats.mrr}`} />
          <Stat label="Total clients" value={stats.total_clients} />
        </div>
      )}
      <div className="bg-card border border-border overflow-x-auto thin-scroll">
        <table className="w-full text-sm">
          <thead><tr className="border-b border-border text-left text-muted-foreground">
            <th className="px-6 py-3 font-medium">Organization</th><th className="px-6 py-3 font-medium">Industry</th>
            <th className="px-6 py-3 font-medium">Plan</th><th className="px-6 py-3 font-medium">Status</th>
            <th className="px-6 py-3 font-medium">Clients</th><th className="px-6 py-3 font-medium text-right">Actions</th>
          </tr></thead>
          <tbody className="divide-y divide-border">
            {orgs.map((o) => (
              <tr key={o.id} className="hover:bg-secondary/40 transition-colors" data-testid={`org-row-${o.id}`}>
                <td className="px-6 py-4"><p className="font-medium">{o.name}{o.fee_waived ? <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-emerald-500/15 text-emerald-600" data-testid={`org-fee-badge-${o.id}`}>Fee waived</span> : (o.fee_override_pct != null ? <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-accent/15 text-accent" data-testid={`org-fee-badge-${o.id}`}>{o.fee_override_pct}% custom</span> : null)}</p><p className="text-xs text-muted-foreground">{o.owner_email}</p></td>
                <td className="px-6 py-4 capitalize text-muted-foreground">{o.industry}</td>
                <td className="px-6 py-4">
                  <select value={o.plan} onChange={(e) => update(o.id, { plan: e.target.value })} data-testid={`org-plan-${o.id}`} className="border border-input bg-background px-2 py-1 text-xs capitalize">
                    {PLAN_OPTS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </td>
                <td className="px-6 py-4"><StatusBadge status={o.subscription_status} /></td>
                <td className="px-6 py-4 tabular-nums">{o.client_count}</td>
                <td className="px-6 py-4"><div className="flex items-center justify-end gap-1">
                  {o.subscription_status === "suspended"
                    ? <button onClick={() => update(o.id, { subscription_status: "active", active: true })} title="Reactivate" data-testid={`org-activate-${o.id}`} className="p-2 hover:bg-secondary text-emerald-600"><CheckCircle2 className="h-4 w-4" /></button>
                    : <button onClick={() => update(o.id, { subscription_status: "suspended", active: false })} title="Suspend" data-testid={`org-suspend-${o.id}`} className="p-2 hover:bg-secondary text-amber-600"><Ban className="h-4 w-4" /></button>}
                  <input type="number" step="0.1" min="0" max="100" defaultValue={o.fee_override_pct ?? ""} placeholder="plan %" title="Custom fee % for this business (blank = use plan). Set -1 and blur to clear." onBlur={(e) => { const v = e.target.value === "" ? -1 : parseFloat(e.target.value); update(o.id, { fee_override_pct: v }); }} data-testid={`org-fee-override-${o.id}`} className="w-16 text-xs bg-background border border-border px-2 py-1" />
                  <button onClick={() => update(o.id, { fee_waived: !o.fee_waived })} title={o.fee_waived ? "Fees waived — click to charge" : "Charging fees — click to waive"} data-testid={`org-fee-waive-${o.id}`} className={`p-2 hover:bg-secondary ${o.fee_waived ? "text-emerald-600" : "text-muted-foreground"}`}><Percent className="h-4 w-4" /></button>
                  <button onClick={() => del(o.id, o.name)} title="Delete" data-testid={`org-delete-${o.id}`} className="p-2 hover:bg-secondary text-destructive"><Trash2 className="h-4 w-4" /></button>
                </div></td>
              </tr>
            ))}
            {orgs.length === 0 && <tr><td colSpan={6} className="px-6 py-16 text-center text-muted-foreground"><Building2 className="h-8 w-8 mx-auto mb-3" />No organizations yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Modal open={openCreate} onClose={() => setOpenCreate(false)} title="Create organization" testid="create-org-modal">
        <form onSubmit={create} className="space-y-4">
          <Field label="Organization name"><input required className={input} value={f.org_name} onChange={(e) => setF({ ...f, org_name: e.target.value })} data-testid="new-org-name" /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Industry"><input className={input} value={f.industry} onChange={(e) => setF({ ...f, industry: e.target.value })} /></Field>
            <Field label="Plan"><select className={input} value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })}>{PLAN_OPTS.map((p) => <option key={p} value={p}>{p}</option>)}</select></Field>
          </div>
          <Field label="Admin name"><input required className={input} value={f.admin_name} onChange={(e) => setF({ ...f, admin_name: e.target.value })} data-testid="new-org-admin" /></Field>
          <Field label="Admin email"><input type="email" required className={input} value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="new-org-email" /></Field>
          <Field label="Temporary password"><input required minLength={6} className={input} value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="new-org-password" /></Field>
          <button type="submit" className={`${btn.primary} w-full`} data-testid="new-org-submit">Create organization</button>
        </form>
      </Modal>
    </div>
  );
}

function useSettings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/platform/settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async (patch) => {
    setSaving(true);
    try { const { data } = await api.put("/platform/settings", patch); setS(data); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return { s, setS, save, saving };
}

function Cms() {
  const { s, setS, save, saving } = useSettings();
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-accent" />;
  const l = s.landing || {};
  const set = (k, v) => setS({ ...s, landing: { ...l, [k]: v } });
  const stats = l.stats || [];
  const setStat = (i, k, v) => set("stats", stats.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  return (
    <div className="max-w-2xl bg-card border border-border p-6 space-y-5">
      <p className="overline text-muted-foreground">Hero</p>
      <Field label="Overline"><input className={input} value={l.overline || ""} onChange={(e) => set("overline", e.target.value)} data-testid="cms-overline" /></Field>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Headline (light)"><input className={input} value={l.headline_light || ""} onChange={(e) => set("headline_light", e.target.value)} data-testid="cms-headline-light" /></Field>
        <Field label="Headline (bold)"><input className={input} value={l.headline_bold || ""} onChange={(e) => set("headline_bold", e.target.value)} data-testid="cms-headline-bold" /></Field>
        <Field label="Headline (tail)"><input className={input} value={l.headline_tail || ""} onChange={(e) => set("headline_tail", e.target.value)} /></Field>
      </div>
      <Field label="Subheadline"><textarea rows={3} className={input} value={l.subheadline || ""} onChange={(e) => set("subheadline", e.target.value)} data-testid="cms-subheadline" /></Field>
      <Field label="Primary button text"><input className={input} value={l.cta_text || ""} onChange={(e) => set("cta_text", e.target.value)} data-testid="cms-cta" /></Field>

      <p className="overline text-muted-foreground pt-2">Stats bar</p>
      <div className="grid grid-cols-2 gap-3">
        {stats.map((st, i) => (
          <div key={i} className="flex gap-2" data-testid={`cms-stat-${i}`}>
            <input className={`${input} w-24`} value={st.value || ""} onChange={(e) => setStat(i, "value", e.target.value)} placeholder="4x" />
            <input className={input} value={st.label || ""} onChange={(e) => setStat(i, "label", e.target.value)} placeholder="faster onboarding" />
          </div>
        ))}
      </div>

      <p className="overline text-muted-foreground pt-2">Section headings</p>
      <Field label="How it works heading"><input className={input} value={l.how_heading || ""} onChange={(e) => set("how_heading", e.target.value)} data-testid="cms-how" /></Field>
      <Field label="Industries heading"><input className={input} value={l.industries_heading || ""} onChange={(e) => set("industries_heading", e.target.value)} data-testid="cms-industries" /></Field>
      <Field label="Features heading"><input className={input} value={l.features_heading || ""} onChange={(e) => set("features_heading", e.target.value)} data-testid="cms-features" /></Field>

      <p className="overline text-muted-foreground pt-2">Call to action & footer</p>
      <Field label="CTA headline"><input className={input} value={l.cta_headline || ""} onChange={(e) => set("cta_headline", e.target.value)} data-testid="cms-cta-headline" /></Field>
      <Field label="CTA subtext"><input className={input} value={l.cta_sub || ""} onChange={(e) => set("cta_sub", e.target.value)} data-testid="cms-cta-sub" /></Field>
      <Field label="Footer note"><input className={input} value={l.footer_note || ""} onChange={(e) => set("footer_note", e.target.value)} data-testid="cms-footer" /></Field>

      <p className="overline text-muted-foreground pt-2">Industry cards</p>
      <div className="space-y-2">
        {(l.industries || []).map((it, i) => (
          <div key={i} className="flex gap-2" data-testid={`cms-industry-${i}`}>
            <input className={`${input} w-40`} value={it.name || ""} onChange={(e) => set("industries", (l.industries || []).map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Name" />
            <input className={input} value={it.desc || ""} onChange={(e) => set("industries", (l.industries || []).map((x, j) => j === i ? { ...x, desc: e.target.value } : x))} placeholder="Description" />
          </div>
        ))}
      </div>

      <p className="overline text-muted-foreground pt-2">Feature cards</p>
      <div className="space-y-2">
        {(l.features || []).map((it, i) => (
          <div key={i} className="flex gap-2" data-testid={`cms-feature-${i}`}>
            <input className={`${input} w-48`} value={it.title || ""} onChange={(e) => set("features", (l.features || []).map((x, j) => j === i ? { ...x, title: e.target.value } : x))} placeholder="Title" />
            <input className={input} value={it.desc || ""} onChange={(e) => set("features", (l.features || []).map((x, j) => j === i ? { ...x, desc: e.target.value } : x))} placeholder="Description" />
          </div>
        ))}
      </div>

      <button onClick={() => save({ landing: s.landing })} disabled={saving} className={btn.primary} data-testid="cms-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save home page</button>
    </div>
  );
}

function Plans() {
  const { s, setS, save, saving } = useSettings();
  const [base, setBase] = useState([]);
  useEffect(() => { api.get("/billing/plans").then((r) => setBase(r.data)).catch(() => {}); }, []);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-accent" />;
  const ov = s.plan_overrides || {};
  const setPlan = (key, patch) => setS({ ...s, plan_overrides: { ...ov, [key]: { ...(ov[key] || {}), ...patch } } });
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-4">
        {base.map((p) => {
          const o = ov[p.key] || {};
          return (
            <div key={p.key} className="bg-card border border-border p-5 space-y-3" data-testid={`plan-edit-${p.key}`}>
              <Field label="Plan name"><input className={input} value={o.name ?? p.name} onChange={(e) => setPlan(p.key, { name: e.target.value })} data-testid={`plan-name-${p.key}`} /></Field>
              <Field label="Price (GBP/mo)"><input type="number" className={input} value={o.price ?? p.price} onChange={(e) => setPlan(p.key, { price: parseInt(e.target.value) || 0 })} data-testid={`plan-price-${p.key}`} /></Field>
              <Field label="Features (one per line)"><textarea rows={6} className={`${input} text-xs`} value={(o.features ?? p.features).join("\n")} onChange={(e) => setPlan(p.key, { features: e.target.value.split("\n").filter(Boolean) })} data-testid={`plan-features-${p.key}`} /></Field>
            </div>
          );
        })}
      </div>
      <button onClick={() => save({ plan_overrides: s.plan_overrides })} disabled={saving} className={btn.primary} data-testid="plans-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save plans</button>
      <p className="text-xs text-muted-foreground">Note: Stripe charge amounts are set in the payment catalog; editing price here updates what's shown on the pricing page.</p>
    </div>
  );
}

function EmailSettings() {
  const { s, setS, save, saving } = useSettings();
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);
  if (!s) return <Loader2 className="h-5 w-5 animate-spin text-accent" />;
  const sm = s.smtp || {};
  const set = (k, v) => setS({ ...s, smtp: { ...sm, [k]: v } });
  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter an email to send the test to"); return; }
    setTesting(true);
    try { await api.post("/platform/settings/test-email", { to: testTo.trim() }); toast.success(`Test email sent to ${testTo}`); }
    catch (e) { toast.error(apiError(e)); } finally { setTesting(false); }
  };
  return (
    <div className="max-w-2xl bg-card border border-border p-6 space-y-4">
      <label className="flex items-center gap-2 text-sm font-medium"><input type="checkbox" checked={!!sm.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="smtp-enabled" /> Enable outbound email (welcome, reminders, decline)</label>
      <div className="grid grid-cols-2 gap-3">
        <Field label="SMTP host"><input className={input} value={sm.host || ""} onChange={(e) => set("host", e.target.value)} placeholder="smtp.yourhost.com" data-testid="smtp-host" /></Field>
        <Field label="Port"><input type="number" className={input} value={sm.port ?? 465} onChange={(e) => set("port", parseInt(e.target.value) || 465)} data-testid="smtp-port" /></Field>
      </div>
      <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={sm.use_ssl !== false} onChange={(e) => set("use_ssl", e.target.checked)} data-testid="smtp-ssl" /> Use SSL (port 465)</label>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Username"><input className={input} value={sm.username || ""} onChange={(e) => set("username", e.target.value)} data-testid="smtp-username" /></Field>
        <Field label="Password"><input type="password" className={input} value={sm.password || ""} onChange={(e) => set("password", e.target.value)} data-testid="smtp-password" /></Field>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Field label="From email"><input className={input} value={sm.from_email || ""} onChange={(e) => set("from_email", e.target.value)} data-testid="smtp-from-email" /></Field>
        <Field label="From name"><input className={input} value={sm.from_name || ""} onChange={(e) => set("from_name", e.target.value)} data-testid="smtp-from-name" /></Field>
      </div>
      <Field label="Auto-remind after (days of inactivity)"><input type="number" className={input} value={sm.reminder_days ?? 3} onChange={(e) => set("reminder_days", parseInt(e.target.value) || 3)} data-testid="smtp-reminder-days" /></Field>
      <button onClick={() => save({ smtp: s.smtp })} disabled={saving} className={btn.primary} data-testid="smtp-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Save email settings</button>

      <div className="border-t border-border pt-4 mt-2">
        <p className="text-sm font-medium">Send a test email</p>
        <p className="text-xs text-muted-foreground mt-1">Save your settings first, then send yourself a test to confirm delivery works.</p>
        <div className="flex items-center gap-2 mt-2">
          <input className={input} type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" data-testid="smtp-test-to" />
          <button onClick={sendTest} disabled={testing} className={btn.outline} data-testid="smtp-test-send">{testing ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Send test</button>
        </div>
      </div>
    </div>
  );
}

function Analytics() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/platform/analytics").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <Loader2 className="h-5 w-5 animate-spin text-accent" />;
  const maxBucket = Math.max(1, ...d.buckets.map((b) => b.count));
  return (
    <div className="space-y-8" data-testid="analytics-panel">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-border border border-border">
        <Stat label="Total clients" value={d.total_clients} />
        <Stat label="Completed" value={d.completed} />
        <Stat label="Completion rate" value={`${d.completion_rate}%`} accent />
        <Stat label="Avg progress" value={`${d.avg_progress}%`} />
      </div>

      <div className="bg-card border border-border p-6">
        <h3 className="font-bold mb-4">Onboarding funnel — where clients are</h3>
        <div className="space-y-3">
          {d.buckets.map((b) => (
            <div key={b.range} className="flex items-center gap-3" data-testid={`bucket-${b.range}`}>
              <span className="w-24 text-sm text-muted-foreground shrink-0">{b.range}</span>
              <div className="flex-1 bg-muted h-7 relative">
                <div className="h-full bg-accent transition-all" style={{ width: `${(b.count / maxBucket) * 100}%` }} />
                <span className="absolute inset-y-0 left-2 flex items-center text-xs font-semibold">{b.count}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-card border border-border">
        <div className="px-6 py-4 border-b border-border"><h3 className="font-bold">Completion rate by organization</h3></div>
        <div className="divide-y divide-border">
          {d.per_org.map((o) => (
            <div key={o.name} className="px-6 py-4 flex items-center gap-4" data-testid={`org-analytics-${o.name}`}>
              <span className="w-48 truncate text-sm font-medium shrink-0">{o.name}</span>
              <div className="flex-1 bg-muted h-6 relative">
                <div className="h-full bg-primary" style={{ width: `${o.completion_rate}%` }} />
              </div>
              <span className="w-28 text-right text-sm text-muted-foreground tabular-nums shrink-0">{o.completed}/{o.total} · {o.completion_rate}%</span>
            </div>
          ))}
          {d.per_org.length === 0 && <div className="px-6 py-10 text-center text-muted-foreground">No organizations yet.</div>}
        </div>
      </div>
    </div>
  );
}

export default function PlatformConsole() {
  const [tab, setTab] = useState("orgs");
  return (
    <div>
      <PageHeader title="Super-Admin" subtitle="Manage the OnboardHQ platform." />
      <div className="flex gap-1 border-b border-border mb-8 overflow-x-auto thin-scroll">
        {TABS.map((t) => (
          <button key={t.k} onClick={() => setTab(t.k)} data-testid={`platform-tab-${t.k}`}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t.k ? "border-accent text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <t.icon className="h-4 w-4" /> {t.l}
          </button>
        ))}
      </div>
      {tab === "orgs" && <Orgs />}
      {tab === "admins" && <Admins />}
      {tab === "analytics" && <Analytics />}
      {tab === "cms" && <Cms />}
      {tab === "plans" && <Plans />}
      {tab === "payments" && <PaymentsSettings />}
      {tab === "email" && <EmailSettings />}
    </div>
  );
}
